import i18n from '@/utils/i18n';
import React from 'react';
import { Badge, Icons, Typography } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeSummaryCounts } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { Pressable, View } from 'react-native';

const ICON_SIZE_PX = 16;

interface EmployeeSummaryProps {
  counts: EmployeeSummaryCounts;
  onInvitePress: () => void;
}

// Keeps the summary card readable on narrow viewports by preventing it from collapsing.
const SUMMARY_MIN_WIDTH = 200;

// Reserves a fixed column width for the count values share width and can be centered vertically across the summary rows
const COUNT_MIN_WIDTH = 16;

export const EmployeeSummary: React.FC<EmployeeSummaryProps> = ({
  counts,
  onInvitePress,
}) => {
  const [styles, theme] = useStyles();
  const { adminCount, inviteCount, totalCount, userCount } = counts;

  const summaryRows = [
    {
      accessibilityLabel: i18n.t(
        'sales_portal.employee_list.admins_count_label',
      ),
      count: adminCount,
      icon: (
        <Icons.UserCheck
          color={theme.sk.color.icon.secondary}
          size={ICON_SIZE_PX}
        />
      ),
      label: i18n.t('sales_portal.employee_list.admins'),
    },
    {
      accessibilityLabel: i18n.t(
        'sales_portal.employee_list.users_count_label',
      ),
      count: userCount,
      icon: (
        <Icons.Users
          color={theme.sk.color.icon.secondary}
          size={ICON_SIZE_PX}
        />
      ),
      label: i18n.t('sales_portal.employee_list.users'),
    },
    {
      accessibilityLabel: i18n.t(
        'sales_portal.employee_list.invites_count_label',
      ),
      count: inviteCount,
      icon: (
        <Icons.CircleDashed
          color={theme.sk.color.icon.secondary}
          size={ICON_SIZE_PX}
        />
      ),
      label: i18n.t('sales_portal.employee_list.invites'),
    },
  ];

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.employee_list.summary_label')}
      style={styles.container}
    >
      <View style={styles.header}>
        <Typography bold size="$sm">
          {i18n.t('sales_portal.employee_list.total_employees')}
        </Typography>
        <View style={styles.badgeWrapper}>
          <Badge color="muted" variant="solid">
            {totalCount}
          </Badge>
        </View>
      </View>
      <View style={styles.countsContainer}>
        {summaryRows.map(({ accessibilityLabel, count, icon, label }) => (
          <View
            accessibilityLabel={accessibilityLabel}
            key={label}
            style={styles.countRow}
          >
            <View style={styles.countLabel}>
              {icon}
              <Typography bold size="$sm">
                {label}
              </Typography>
            </View>
            <View style={styles.countWrapper}>
              <Typography size="$sm" textAlign="center">
                {count}
              </Typography>
            </View>
          </View>
        ))}
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={onInvitePress}
        style={({ hovered }) => [
          styles.inviteRow,
          hovered ? styles.inviteRowHovered : undefined,
        ]}
      >
        <Icons.Plus color={theme.sk.color.icon.primary} size={ICON_SIZE_PX} />
        <Typography size="$sm">
          {i18n.t('sales_portal.employee_list.invite_employee')}
        </Typography>
      </Pressable>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  badgeWrapper: {
    alignSelf: 'center',
  },
  container: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.spacing.spaceSmall,
    borderWidth: 1,
    minWidth: SUMMARY_MIN_WIDTH,
  },
  countLabel: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  countRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  countsContainer: {
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  countWrapper: {
    minWidth: COUNT_MIN_WIDTH,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  inviteRow: {
    alignItems: 'center',
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  inviteRowHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
}));
